<script>
import { mapActions } from "vuex";

import moment from "@/utils/moment";

import professionalRegisterFlowMixin from "@/mixins/professionalRegisterFlowMixin";
import documentMixin from "@/mixins/documentMixin";

export default {
  mixins: [professionalRegisterFlowMixin],

  props: {
    professional: {
      type: Object,
      required: true
    },
    events: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      preloader: false
    };
  },

  methods: {
    ...mapActions("management", [
      "requestDocumentsValidation",
      "askForDocuments",
      "requestBackgroundCheck",
      "denyAccess"
    ]),
    ...mapActions("documents", ["checkDocumentOnDataAssuranceService"]),

    formattedDate(datetime) {
      return moment(datetime).format("DD MMMM YYYY HH:mm:ss");
    },

    async sendDocsToAnalysis() {
      this.preloader = true;

      try {
        const response = await this.requestDocumentsValidation(
          this.professional.id
        );

        if (response.status === 200) {
          this.$toast.success("Análise dos antecedentes solicitada!");
        } else {
          this.$toast.error(
            "Não foi possível solicitar a análise dos antecedentes no CAF!"
          );
        }
      } catch (e) {
        this.$toast.error(e);
      }

      this.preloader = false;
      this.$emit("refresh");
    },

    async requestNewDocuments() {
      this.preloader = true;

      try {
        await this.askForDocuments(this.professional.id);

        this.$toast.success("Novos documentos solicitados!");
      } catch (e) {
        this.$toast.error(
          "Não foi possível solicitar novos documentos para análise"
        );
      }

      this.preloader = false;
      this.$emit("refresh");
    },

    async denyProfessionalAccess() {
      this.preloader = true;

      try {
        await this.denyAccess(this.professional.id);

        this.$toast.success("Acesso do profissional negado.");
      } catch (e) {
        this.$toast.error("Não foi possível negar o acesso do profissional");
      }

      this.$emit("refresh");
      this.preloader = false;
    },

    async checkDocOnDataAssuranceService() {
      this.preloader = true;

      const document = this.getOcrDocument();
      const reportId = documentMixin.methods.reportIdByDocumentType(
        document.document_type
      );

      try {
        const response = await this.checkDocumentOnDataAssuranceService({
          data_assurance_report_id: reportId,
          doc_external_id: document.external_id
        });
        if (response.status === 200) {
          this.$toast.success(
            "Atualização junto ao CAF realizada com sucesso!"
          );
        } else {
          this.$toast.error(
            "Tivemos um erro ao atualizar os documentos junto ao CAF :/"
          );
        }
      } catch (error) {
        this.$toast.error(error);
      }
      this.preloader = false;
      this.$emit("refresh");
    },

    async checkBackgroundCheck() {
      this.preloader = true;

      try {
        const response = await this.requestBackgroundCheck(
          this.professional.id
        );

        if (response.status === 200) {
          this.$toast.success("Análise dos antecedentes solicitada!");
        } else {
          this.$toast.error(
            "Não foi possível solicitar a análise dos antecedentes no CAF!"
          );
        }
      } catch (e) {
        this.$toast.error(e);
      }
      this.preloader = false;
      this.$emit("refresh");
    },

    getOcrDocument() {
      return this.professional.account.documents.find(
        doc =>
          documentMixin.methods.isOcrDocument(doc.document_type) &&
          doc.external_id != null
      );
    }
  },

  computed: {
    isWaitingForDocuments: function() {
      return this.professional.status === "waiting_for_documents";
    },

    isValidDocuments: function() {
      return this.professional.status === "valid_documents";
    },

    isWaitingDocumentsValidation: function() {
      return this.professional.status === "waiting_documents_validation";
    },

    isInvalidDocuments: function() {
      return this.professional.status === "invalid_documents";
    },

    canSendDocsToAnalysis: function() {
      const docs = this.professional.account.documents.filter(doc =>
        documentMixin.methods.isOcrDocument(doc.document_type)
      );

      if (docs.length !== 2 || docs.some(doc => doc.status === "created")) {
        return false;
      }

      return true;
    }
  }
};
</script>

<template>
  <div class="body">
    <div class="status-details">
      <div class="description">
        Nessa etapa o profissional deve enviar os seus documentos e nós devemos
        enviá-lo para o serviço que verifica a sua autenticidade. Caso o
        documentos se encontre no estado de reprovado você poderá
        <b>recusar</b> o cadastro do profissional negando o seu acesso a
        plataforma ou <b>solicitar um novo envio</b>.
      </div>

      <div class="events">
        <ul>
          <li v-for="event in events">
            <b>{{ eventName(event.event_type) | getStatusName }}</b>

            <span>{{ formattedDate(event.created_at) }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="actions">
      <v-tooltip
        bottom
        v-if="isWaitingForDocuments"
        :disabled="canSendDocsToAnalysis"
      >
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <v-btn
              color="primary"
              small
              :disabled="!canSendDocsToAnalysis"
              :loading="preloader"
              @click="sendDocsToAnalysis"
            >
              Enviar documentos para análise
            </v-btn>
          </span>
        </template>

        <span
          >O profissional ainda não enviou os documentos ou eles foram
          recusados.</span
        >
      </v-tooltip>

      <div bottom v-if="isValidDocuments">
        <v-btn
          color="primary"
          small
          :loading="preloader"
          @click="checkBackgroundCheck"
        >
          Solicitar análise dos antecedentes
        </v-btn>
      </div>

      <div bottom v-if="isWaitingDocumentsValidation">
        <v-btn
          color="primary"
          small
          :loading="preloader"
          @click="checkDocOnDataAssuranceService"
        >
          Atualizar status dos documentos no CAF
        </v-btn>
      </div>

      <div bottom v-if="isInvalidDocuments">
        <v-btn
          color="primary"
          small
          :loading="preloader"
          @click="requestNewDocuments"
        >
          Solicitar novo envio de documentos
        </v-btn>

        <v-btn
          color="error"
          small
          :loading="preloader"
          @click="denyProfessionalAccess"
        >
          Negar acesso a plataforma
        </v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.body {
  padding: 16px;
}

.status-details {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: solid #424242 1px;
}

.description {
  width: 35%;
  text-align: center;
}

.events {
  width: 60%;
  text-decoration: none;
}

li {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 4px 8px;
  list-style: none;
}

li:nth-child(even) {
  background: #eee;
}
.actions {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.action {
  display: flex;
  flex-direction: column;
}

span {
  font-size: 11px;
}
</style>



// WEBPACK FOOTER //
// src/components/Professionals/Edit/Tabs/Register/Steps/DocumentValidation.vue